import type { SupabaseClient } from "@supabase/supabase-js";

// PRD-001I.6 manager queue. Reads and writes use only the dedicated RPCs from
// 20260801170000. The table stays RPC-only, and proposer identity is deliberately
// omitted from the client model because the resolve workflow does not need it.

export type CheckpointRequestConvertMode = "accept_guard_draft" | "re_place";

export interface CheckpointRequestRow {
  requestId: string;
  siteId: string;
  siteName: string;
  proposedName: string;
  proposedLat: number;
  proposedLng: number;
  captureAccuracyMeters: number;
  accuracyWarning: boolean;
  notePreview: string | null;
  createdAt: string | null;
}

export type CheckpointRequestWriteOutcome =
  | { ok: true; checkpointId?: string }
  | { ok: false; reason: string };

export interface CheckpointRequestConversion {
  mode: CheckpointRequestConvertMode;
  nameOverride: string;
  managerLat: number | null;
  managerLng: number | null;
  managerAccuracyMeters: number | null;
}

export const SAFE_CHECKPOINT_REQUEST_READ_FAILURE = "Could not load guard proposals.";
export const SAFE_CHECKPOINT_REQUEST_WRITE_FAILURE =
  "Couldn't update that guard proposal - please try again.";

const LIST_LIMIT = 100;
const UUID_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/giu;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/giu;

const KNOWN_CHECKPOINT_REQUEST_FAILURES: ReadonlySet<string> = new Set([
  "Schedule management permission is required to reject checkpoint proposals.",
  "Schedule management permission is required to convert checkpoint proposals.",
  "Checkpoint proposal id is required.",
  "Checkpoint proposal id and convert mode are required.",
  "Rejection reason must be 500 characters or fewer.",
  "Checkpoint proposal was not found for this account.",
  "Only a proposed checkpoint request can be rejected.",
  "Only a proposed checkpoint request can be converted.",
  "An open checkpoint proposal was not found for this account.",
  "Checkpoint proposal site is no longer active.",
  "Checkpoint name must be between 1 and 80 characters.",
  "Manager coordinates are not accepted in accept_guard_draft mode.",
  "Manager re-placement coordinates are required and must be in range.",
  "Manager re-placement accuracy is required and must be nonnegative.",
  "Manager GPS accuracy does not meet the account placement capture threshold.",
  "Checkpoint proposal changed while conversion was in progress.",
]);

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Remove identity-shaped values before any manager-authored text reaches visible DOM copy. */
export function redactCheckpointRequestText(value: string): string {
  return value.replace(UUID_PATTERN, "[redacted]").replace(EMAIL_PATTERN, "[redacted]");
}

function visibleString(value: unknown, fallback: string): string {
  const parsed = asString(value);
  if (parsed === null) return fallback;
  const redacted = redactCheckpointRequestText(parsed).trim();
  return redacted.length > 0 ? redacted : fallback;
}

/** Short, identity-redacted preview. The full note is never added to the DOM as a title. */
export function checkpointRequestNotePreview(note: string, maxLength = 120): string {
  const redacted = redactCheckpointRequestText(note).replace(/\s+/gu, " ").trim();
  if (redacted.length <= maxLength) return redacted;
  return `${redacted.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
}

export function formatCheckpointRequestRelativeTime(
  createdAt: string | null,
  nowMs = Date.now(),
): string {
  if (createdAt === null) return "Time unavailable";
  const createdMs = Date.parse(createdAt);
  if (!Number.isFinite(createdMs)) return "Time unavailable";
  const elapsedSeconds = Math.max(0, Math.floor((nowMs - createdMs) / 1000));
  if (elapsedSeconds < 60) return "Just now";
  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export async function fetchCheckpointRequests(
  client: SupabaseClient,
  accountId: string,
): Promise<CheckpointRequestRow[]> {
  try {
    const { data, error } = await client.rpc("list_account_checkpoint_requests", {
      p_account_id: accountId,
      p_status: "proposed",
      p_limit: LIST_LIMIT,
    });
    if (error !== null || !Array.isArray(data)) {
      throw new Error(SAFE_CHECKPOINT_REQUEST_READ_FAILURE);
    }

    const rows: CheckpointRequestRow[] = [];
    for (const raw of data as Array<Record<string, unknown>>) {
      const requestId = asString(raw.request_id);
      const siteId = asString(raw.site_id);
      const proposedLat = asNumber(raw.proposed_lat);
      const proposedLng = asNumber(raw.proposed_lng);
      const captureAccuracyMeters = asNumber(raw.capture_accuracy_m);
      if (
        requestId === null ||
        siteId === null ||
        proposedLat === null ||
        proposedLng === null ||
        captureAccuracyMeters === null ||
        proposedLat < -90 ||
        proposedLat > 90 ||
        proposedLng < -180 ||
        proposedLng > 180 ||
        captureAccuracyMeters < 0 ||
        raw.request_status !== "proposed"
      ) {
        continue;
      }
      const rawNote = asString(raw.note);
      rows.push({
        requestId,
        siteId,
        siteName: visibleString(raw.site_name, "Unknown site"),
        proposedName: visibleString(raw.proposed_name, "Checkpoint proposal"),
        proposedLat,
        proposedLng,
        captureAccuracyMeters,
        accuracyWarning: raw.accuracy_warning === true,
        notePreview: rawNote === null ? null : checkpointRequestNotePreview(rawNote),
        createdAt: asString(raw.created_at),
      });
    }
    return rows;
  } catch {
    throw new Error(SAFE_CHECKPOINT_REQUEST_READ_FAILURE);
  }
}

function safeWriteFailure(error: unknown): string {
  const message =
    typeof error === "object" && error !== null && "message" in error
      ? (error as { message?: unknown }).message
      : null;
  return typeof message === "string" && KNOWN_CHECKPOINT_REQUEST_FAILURES.has(message)
    ? message
    : SAFE_CHECKPOINT_REQUEST_WRITE_FAILURE;
}

export async function rejectCheckpointRequest(
  client: SupabaseClient,
  accountId: string,
  requestId: string,
  reason: string | null,
): Promise<CheckpointRequestWriteOutcome> {
  const cleanReason = reason?.trim() || null;
  if (cleanReason !== null && cleanReason.length > 500) {
    return { ok: false, reason: "Rejection reason must be 500 characters or fewer." };
  }
  try {
    const { data, error } = await client.rpc("reject_checkpoint_request", {
      p_account_id: accountId,
      p_request_id: requestId,
      p_reason: cleanReason,
    });
    if (error !== null) return { ok: false, reason: safeWriteFailure(error) };
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    if (row?.request_status !== "rejected") {
      return { ok: false, reason: SAFE_CHECKPOINT_REQUEST_WRITE_FAILURE };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_CHECKPOINT_REQUEST_WRITE_FAILURE };
  }
}

function validReplacement(input: CheckpointRequestConversion): boolean {
  return (
    input.managerLat !== null &&
    Number.isFinite(input.managerLat) &&
    input.managerLat >= -90 &&
    input.managerLat <= 90 &&
    input.managerLng !== null &&
    Number.isFinite(input.managerLng) &&
    input.managerLng >= -180 &&
    input.managerLng <= 180
  );
}

export async function convertCheckpointRequest(
  client: SupabaseClient,
  accountId: string,
  requestId: string,
  input: CheckpointRequestConversion,
): Promise<CheckpointRequestWriteOutcome> {
  const nameOverride = visibleString(input.nameOverride, "Checkpoint proposal");
  if (nameOverride.length > 80) {
    return { ok: false, reason: "Checkpoint name must be between 1 and 80 characters." };
  }
  if (input.mode === "re_place" && !validReplacement(input)) {
    return {
      ok: false,
      reason: "Manager re-placement coordinates are required and must be in range.",
    };
  }
  if (
    input.mode === "re_place" &&
    (input.managerAccuracyMeters === null ||
      !Number.isFinite(input.managerAccuracyMeters) ||
      input.managerAccuracyMeters < 0)
  ) {
    return {
      ok: false,
      reason: "Manager re-placement accuracy is required and must be nonnegative.",
    };
  }

  const managerLat = input.mode === "re_place" ? input.managerLat : null;
  const managerLng = input.mode === "re_place" ? input.managerLng : null;
  const managerAccuracyMeters =
    input.mode === "re_place" ? input.managerAccuracyMeters : null;

  try {
    const { data, error } = await client.rpc("convert_checkpoint_request", {
      p_account_id: accountId,
      p_request_id: requestId,
      p_convert_mode: input.mode,
      p_manager_lat: managerLat,
      p_manager_lng: managerLng,
      p_manager_accuracy_m: managerAccuracyMeters,
      // Persist the same identity-redacted name shown in the queue. Leaving this null would
      // restore the raw guard-authored name when the converted draft reloads.
      p_name_override: nameOverride,
    });
    if (error !== null) return { ok: false, reason: safeWriteFailure(error) };
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    const checkpointId = asString(row?.checkpoint_id);
    if (
      row?.request_status !== "converted" ||
      row.convert_mode !== input.mode ||
      row.checkpoint_placement_state !== "provisional" ||
      row.checkpoint_placement_source !== "field_guard_proposal" ||
      checkpointId === null
    ) {
      return { ok: false, reason: SAFE_CHECKPOINT_REQUEST_WRITE_FAILURE };
    }
    return { ok: true, checkpointId };
  } catch {
    return { ok: false, reason: SAFE_CHECKPOINT_REQUEST_WRITE_FAILURE };
  }
}
